import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError, del, errorMessage, get, patch, post } from '../../api/client';
import type { LineupFamily, LineupModel, LineupState, LineupTexts, LineupVersion, Media, NewsLang } from '../../api/types';
import { useMe } from '../../auth';
import { IconAlert, IconArrowLeft, IconTrash } from '../../components/icons';
import { Field, formatDate, Spinner, Switch, useFeedback } from '../../components/ui';
import { useDraft, useNavigationGuard, useUnloadGuard } from '../../hooks';
import { tk, useT } from '../../i18n';
import { SaveBar } from '../model/SaveBar';
import { hasFicha, LINEUP_KEY } from './Lineup';
import { LineupPhoto } from './LineupPhoto';
import { emptyLineupTexts, fichaState, FIGS, LANGS, LINEUP_LIMITS } from './shared';

interface Form {
  name: string;
  familyId: string;
  versions: LineupVersion[];
  euro: string;
  launch: boolean;
  limited: boolean;
  start: boolean;
  image: Media | null;
  texts: LineupTexts;
  visible: boolean;
}

const fromModel = (m: LineupModel | null, familyId: string): Form =>
  m
    ? { name: m.name, familyId: m.familyId, versions: m.versions, euro: m.euro, launch: m.launch, limited: m.limited, start: m.start, image: m.image, texts: m.texts, visible: m.visible }
    : { name: '', familyId, versions: ['eu', 'global'], euro: 'EURO 5+', launch: false, limited: false, start: false, image: null, texts: emptyLineupTexts(), visible: true };

const VERSIONS: { code: LineupVersion; label: string; hint: string }[] = [
  { code: 'eu', label: tk('Europa'), hint: tk('/europe/, /pl/, /it/, /pt/, /el/') },
  { code: 'global', label: tk('Global'), hint: tk('/ y /es/ (el resto del mundo)') },
];

/** Etiquetas de las cifras de ejemplo (las de la Capetown 7X), para los textos de ayuda. */
const FIG_EXAMPLES = [
  ['693 cc', 'Parallel twin, 8 valves DOHC'],
  ['74 hp', '68 Nm at 7,000 rpm'],
  ['A2', '35 kW version available'],
  ['21 L', 'Fuel tank'],
];

export function LineupEditor() {
  const { t } = useT();
  const { id = 'nueva' } = useParams();
  const isNew = id === 'nueva';
  const [params] = useSearchParams();
  const list = useQuery({ queryKey: LINEUP_KEY, queryFn: () => get<LineupState>('/admin/pm/lineup') });
  const model = useQuery({
    queryKey: ['pm-lineup', 'model', id],
    queryFn: async () => (await get<{ model: LineupModel }>(`/admin/pm/lineup/models/${id}`)).model,
    enabled: !isNew,
  });
  if (list.isPending || (!isNew && model.isPending)) return <Spinner />;
  if (list.error || !list.data) return <div className="notice error">{errorMessage(list.error)}</div>;
  if (!isNew && (model.error || !model.data)) return <div className="notice error">{errorMessage(model.error)}</div>;
  const families = list.data.families;
  if (isNew && !families.length) {
    return (
      <>
        <ModelBack />
        <div className="notice warn">
          {list.data.ready
            ? t('Para crear una moto, primero crea su familia (Motos → Nueva familia).')
            : t('Falta aplicar la migración de la base para guardar motos (npm run db:deploy). Mientras tanto la landing sigue con la gama de su código.')}
        </div>
      </>
    );
  }
  const wanted = params.get('familia');
  const familyId = families.some((f) => f.id === wanted) ? wanted! : families[0]!.id;
  return <ModelForm key={id} model={isNew ? null : model.data!} families={families} defaultFamily={familyId} />;
}

function ModelBack() {
  const { t } = useT();
  return (
    <Link to="/motos" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
      <IconArrowLeft /> {t('Motos')}
    </Link>
  );
}

function ModelForm({ model, families, defaultFamily }: { model: LineupModel | null; families: LineupFamily[]; defaultFamily: string }) {
  const { t, tn, tr } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const initial = useMemo(() => fromModel(model, defaultFamily), [model, defaultFamily]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial);
  const [lang, setLang] = useState<NewsLang>('en');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [leaving, setLeaving] = useState(false);
  useUnloadGuard(dirty && !leaving);
  useNavigationGuard(dirty && !leaving);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setF((prev) => ({ ...prev, [key]: value }));
  const setFicha = (l: NewsLang, change: Partial<LineupTexts[NewsLang]>) =>
    setF((prev) => ({ ...prev, texts: { ...prev.texts, [l]: { ...prev.texts[l], ...change } } }));
  const setFig = (l: NewsLang, i: number, part: 0 | 1, value: string) =>
    setF((prev) => {
      const figs = prev.texts[l].figs.map((pair, k) => (k === i ? (part === 0 ? [value, pair[1]] : [pair[0], value]) : pair)) as [string, string][];
      return { ...prev, texts: { ...prev.texts, [l]: { ...prev.texts[l], figs } } };
    });
  const toggleVersion = (v: LineupVersion, on: boolean) =>
    set('versions', (['eu', 'global'] as LineupVersion[]).filter((x) => (x === v ? on : f.versions.includes(x))));

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: LINEUP_KEY });
    void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  const save = async () => {
    const submitted = f;
    setBusy(true);
    setError(null);
    const body = {
      familyId: f.familyId,
      name: f.name.trim(),
      versions: f.versions,
      euro: f.euro.trim(),
      launch: f.launch,
      limited: f.limited,
      start: f.start,
      imageMediaId: f.image?.id ?? null,
      texts: f.texts,
      visible: f.visible,
    };
    try {
      if (!model) {
        const { model: created } = await post<{ model: LineupModel }>('/admin/pm/lineup/models', body);
        qc.setQueryData(['pm-lineup', 'model', created.id], created);
        refresh();
        toast(t('Moto creada al final de su familia. Se verá en el sitio después de publicar.'));
        // Sin la guarda de "cambios sin guardar": ya se guardó todo.
        flushSync(() => setLeaving(true));
        navigate(`/motos/${created.id}`, { replace: true });
        return;
      }
      const { model: updated } = await patch<{ model: LineupModel }>(`/admin/pm/lineup/models/${model.id}`, body);
      markSaved(submitted);
      qc.setQueryData(['pm-lineup', 'model', model.id], updated);
      refresh();
      toast(t('Guardado. Se verá en el sitio después de publicar.'));
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, errorMessage(e));
      setError(err);
      // Si el error es de la ficha, se abre la pestaña de su idioma.
      const bad = LANGS.find((l) => err.field(`texts.${l.code}`));
      if (bad) setLang(bad.code);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!model) return;
    const ok = await confirm(
      tr('¿Borrar la moto {name}? Sale del sitio en la próxima publicación. Si solo quieres sacarla un tiempo, ocúltala.', {
        name: <strong>{t('«{name}»', { name: model.name })}</strong>,
      }),
      { confirmLabel: t('Borrar'), danger: true },
    );
    if (!ok) return;
    try {
      await del(`/admin/pm/lineup/models/${model.id}`);
      toast(t('Moto borrada'));
      refresh();
      flushSync(() => setLeaving(true));
      navigate('/motos', { replace: true });
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  const family = families.find((x) => x.id === f.familyId);
  const errors = model?.issues.filter((i) => i.level === 'error') ?? [];
  const warnings = model?.issues.filter((i) => i.level === 'warning') ?? [];
  const ficha = f.texts[lang];
  const en = f.texts.en;
  const fieldError = (path: string) => error?.field(path);
  const eqLines = ficha.eq.filter((x) => x.trim()).length;
  const withFicha = hasFicha(f);

  return (
    <>
      <ModelBack />
      <header className="page-head">
        <div>
          <p className="kicker">{model ? t('Motos · {family}', { family: family?.name ?? '' }) : t('Motos · moto nueva')}</p>
          <h1>{f.name.trim() || t('Nueva moto')}</h1>
          {model && (
            <p className="faint" style={{ fontSize: 13 }}>
              {t('Última edición: {date}', { date: formatDate(model.updatedAt) })}
            </p>
          )}
        </div>
        <div className="actions">
          <Switch checked={f.visible} onChange={(v) => set('visible', v)} label={f.visible ? t('Visible en el sitio') : t('Oculta')} />
          {model && me?.role === 'ADMIN' && (
            <button className="btn btn-danger" onClick={() => void remove()}>
              <IconTrash /> {t('Borrar')}
            </button>
          )}
        </div>
      </header>

      {(errors.length > 0 || warnings.length > 0) && (
        <div className={`notice ${errors.length ? 'error' : 'warn'}`} style={{ marginBottom: 18, display: 'block' }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>
            {errors.length ? t('Falta completar para poder publicarla visible') : t('Avisos (no impiden publicar)')}
          </strong>
          <ul className="issues">
            {[...errors, ...warnings].map((i) => (
              <li key={i.message}>
                <IconAlert className="ico" style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--warn)' }} />
                <span>{i.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && !error.errors.length && <div className="notice error" style={{ marginBottom: 18 }}>{error.message}</div>}

      <div className="news-editor">
        <section className="panel panel-pad stack">
          <div>
            <h2 className="section-title">{t('Foto')}</h2>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {t('Sube la moto sin fondo (PNG transparente), de perfil y mirando a la derecha como las demás. Al publicar, la web la encuadra sola: todas quedan del mismo ancho y apoyadas en la misma línea.')}
            </p>
          </div>
          <LineupPhoto media={f.image} onMedia={(m) => set('image', m)} error={fieldError('imageMediaId')} launch={f.launch} />
        </section>

        <section className="panel panel-pad stack">
          <h2 className="section-title">{t('La moto')}</h2>
          <Field
            label={t('Nombre')}
            error={fieldError('name')}
            count={{ value: f.name, max: LINEUP_LIMITS.name }}
            hint={t('Igual en los seis idiomas. Va en mayúsculas debajo de la foto y como título de la ficha.')}
          >
            {(id) => <input id={id} className="input" value={f.name} placeholder="Capetown 7X" onChange={(e) => set('name', e.target.value)} />}
          </Field>
          <div className="grid grid-2">
            <Field
              label={t('Familia')}
              error={fieldError('familyId')}
              hint={model ? t('Si la cambias, la moto pasa al final de la otra familia.') : t('La moto nueva va al final de su familia; después se mueve arrastrándola en la lista.')}
            >
              {(id) => (
                <select id={id} className="select" value={f.familyId} onChange={(e) => set('familyId', e.target.value)}>
                  {families.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} · {x.texts.en.segment}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('Homologación')} error={fieldError('euro')} count={{ value: f.euro, max: LINEUP_LIMITS.euro }} hint={t('De referencia: la web no la muestra (EURO 5+, EURO 4…).')}>
              {(id) => <input id={id} className="input" value={f.euro} placeholder="EURO 5+" onChange={(e) => set('euro', e.target.value)} />}
            </Field>
          </div>
          <div className="field">
            <span className="field-label">{t('Sale en')}</span>
            <div className="site-toggles">
              {VERSIONS.map((v) => (
                <label key={v.code} className={`site-toggle ${f.versions.includes(v.code) ? 'on' : ''}`} title={t(v.hint)}>
                  <input type="checkbox" checked={f.versions.includes(v.code)} onChange={(e) => toggleVersion(v.code, e.target.checked)} />
                  {t(v.label)}
                </label>
              ))}
            </div>
            {fieldError('versions') ? (
              <div className="error">{fieldError('versions')}</div>
            ) : (
              <div className="hint">{t('Las versiones de la landing: Europa (/europe/, /pl/, /it/, /pt/, /el/) y Global (/ y /es/). En Europa todas son EURO 5+.')}</div>
            )}
          </div>
          <div className="stack lineup-flags" style={{ gap: 10 }}>
            <Flag
              checked={f.launch}
              onChange={(v) => set('launch', v)}
              label={t('Lanzamiento')}
              hint={t('Lleva la etiqueta «EICMA 2026 launch» sobre la foto y en la ficha. Sin foto sale como silueta oscura, y sin ficha.')}
            />
            <Flag checked={f.limited} onChange={(v) => set('limited', v)} label={t('Edición limitada')} hint={t('La ficha lleva la etiqueta «Limited edition».')} />
            <Flag
              checked={f.start}
              onChange={(v) => set('start', v)}
              label={t('Ficha inicial')}
              hint={t('La ficha que se ve al abrir la página. Es una sola: al marcarla aquí deja de serlo la que lo era.')}
            />
          </div>
        </section>

        <section className="panel panel-pad news-texts">
          <div className="row between row-wrap" style={{ marginBottom: 6 }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              {t('Ficha')}
            </h2>
            <span className="faint" style={{ fontSize: 13 }}>
              {t('El inglés es obligatorio para publicarla visible. Lo que quede vacío en otro idioma sale en inglés en el sitio.')}
            </span>
          </div>
          {!withFicha && (
            <div className="notice" style={{ marginBottom: 14 }}>
              {t('Un lanzamiento sin foto sale solo como silueta en la gama: la ficha no se ve hasta que tenga foto. Puedes dejarla lista desde ya.')}
            </div>
          )}
          <div className="tabs lang-tabs" role="tablist">
            {LANGS.map((l) => {
              const state = fichaState(f.texts, l.code);
              const bad = !!error?.field(`texts.${l.code}`);
              return (
                <button key={l.code} role="tab" className="tab" aria-selected={lang === l.code} onClick={() => setLang(l.code)}>
                  <span className={`lang-dot ${bad ? 'bad' : state}`} aria-hidden="true" />
                  {t(l.name)}
                </button>
              );
            })}
          </div>
          <div className="stack">
            <Field
              label={lang === 'en' ? t('Frase') : t('{label} (opcional)', { label: t('Frase') })}
              error={fieldError(`texts.${lang}.tag`)}
              count={{ value: ficha.tag, max: LINEUP_LIMITS.tag }}
              hint={t('Una línea debajo del nombre: «Adventure ready, from asphalt to gravel.»')}
            >
              {(id) => (
                <input
                  id={id}
                  lang={lang}
                  className={`input ${fieldError(`texts.${lang}.tag`) ? 'invalid' : ''}`}
                  value={ficha.tag}
                  placeholder={lang !== 'en' && en.tag ? t('En inglés: {text}', { text: en.tag }) : undefined}
                  onChange={(e) => setFicha(lang, { tag: e.target.value })}
                />
              )}
            </Field>

            <div className="field">
              <span className="field-label">
                <span>{lang === 'en' ? t('Cifras') : t('{label} (opcional)', { label: t('Cifras') })}</span>
              </span>
              <div className="figs-editor">
                {Array.from({ length: FIGS }, (_, i) => {
                  const [v, l] = ficha.figs[i] ?? ['', ''];
                  const [ev, el] = en.figs[i] ?? ['', ''];
                  const vErr = fieldError(`texts.${lang}.figs.${i}.0`);
                  const lErr = fieldError(`texts.${lang}.figs.${i}.1`);
                  return (
                    <div key={i} className="fig-row">
                      <span className="fig-n" aria-hidden="true">
                        {i + 1}
                      </span>
                      <input
                        className={`input fig-value ${vErr ? 'invalid' : ''}`}
                        lang={lang}
                        value={v}
                        aria-label={t('Cifra {n}: valor', { n: i + 1 })}
                        placeholder={lang === 'en' ? FIG_EXAMPLES[i]![0] : ev || undefined}
                        onChange={(e) => setFig(lang, i, 0, e.target.value)}
                      />
                      <input
                        className={`input ${lErr ? 'invalid' : ''}`}
                        lang={lang}
                        value={l}
                        aria-label={t('Cifra {n}: etiqueta', { n: i + 1 })}
                        placeholder={lang === 'en' ? FIG_EXAMPLES[i]![1] : el || undefined}
                        onChange={(e) => setFig(lang, i, 1, e.target.value)}
                      />
                      {(vErr || lErr) && <div className="error fig-error">{vErr ?? lErr}</div>}
                    </div>
                  );
                })}
              </div>
              {fieldError(`texts.${lang}.figs`) && !ficha.figs.some((_, i) => fieldError(`texts.${lang}.figs.${i}`)) ? (
                <div className="error">{fieldError(`texts.${lang}.figs`)}</div>
              ) : (
                <div className="hint">
                  {lang === 'en'
                    ? t('Cuatro casillas, como el diseño: el valor grande (hasta {value} caracteres) y su etiqueta debajo (hasta {label}).', { value: LINEUP_LIMITS.figValue, label: LINEUP_LIMITS.figLabel })
                    : t('Los valores vacíos salen como en inglés (sirve para «A2» o «CVT»); escríbelos si cambian, p. ej. «449,5 cc» o «74 CV».')}
                </div>
              )}
            </div>

            <Field
              label={lang === 'en' ? t('Equipamiento de serie') : t('{label} (opcional)', { label: t('Equipamiento de serie') })}
              error={fieldError(`texts.${lang}.eq`)}
              hint={
                <>
                  {t('Una línea por elemento (hasta {max} líneas). En la ficha se ven las cuatro primeras y el resto con «See all…».', { max: LINEUP_LIMITS.eqItems })}{' '}
                  <span className={eqLines > LINEUP_LIMITS.eqItems ? 'error' : ''}>{tn(eqLines, '{n} línea', '{n} líneas')}</span>
                </>
              }
            >
              {(id) => (
                <textarea
                  id={id}
                  lang={lang}
                  rows={Math.min(LINEUP_LIMITS.eqItems + 1, Math.max(6, ficha.eq.length + 1))}
                  className={`textarea ${fieldError(`texts.${lang}.eq`) ? 'invalid' : ''}`}
                  value={ficha.eq.join('\n')}
                  placeholder={lang !== 'en' && en.eq.length ? `${t('En inglés:')}\n${en.eq.filter((x) => x.trim()).join('\n')}` : 'Top case and side panniers as standard\nSpoked tubeless wheels, 19/17\n…'}
                  onChange={(e) => setFicha(lang, { eq: e.target.value.split('\n') })}
                />
              )}
            </Field>
          </div>
        </section>
      </div>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={() => void save()} onReset={reset} />
    </>
  );
}

function Flag({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <label className="lineup-flag">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        <strong>{label}</strong>
        <span className="faint">{hint}</span>
      </span>
    </label>
  );
}
